import React from 'react';
import './App.css';
import UniversalHeader from './UniversalHeader';
import { useNavigate } from 'react-router-dom';
import { supabase } from './supabaseClient';
import MaintenanceAssignStaffModal from './MaintenanceAssignStaffModal';

function staffFromRequest(row) {
  const rel = row.maintenance_staff;
  return Array.isArray(rel) ? rel[0] : rel;
}

function assigneeDisplay(row) {
  const s = staffFromRequest(row);
  if (s?.full_name) {
    const name = (s.full_name || '').trim();
    const role = (s.role || '').trim();
    return role ? `${name} (${role})` : name;
  }
  return (row.assignee || '').trim() || '—';
}

function displayProperty(row) {
  const rel = row.properties;
  const p = Array.isArray(rel) ? rel[0] : rel;
  if (p?.name) return p.location ? `${p.name} (${p.location})` : p.name;
  return row.property_name?.trim() || '—';
}

function mapRowToRequest(row) {
  return {
    id: row.id,
    tenant: row.tenant_name || '—',
    property: displayProperty(row),
    room: row.room_label || '—',
    issue: row.issue_description || '',
    status: row.status || 'pending',
    date: row.logged_on || row.created_at,
    assignee: assigneeDisplay(row),
    assignedStaffId: row.assigned_staff_id || null,
  };
}

function initials(name) {
  const parts = (name || '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0] || ''}${parts[parts.length - 1][0] || ''}`.toUpperCase();
}

function mediaKindFromUrl(url) {
  if (!url) return 'image';
  return /\.(mp4|webm|mov)(\?|$)/i.test(url) ? 'video' : 'image';
}

function Maintenance() {
  const [search, setSearch] = React.useState('');
  const [statusFilter, setStatusFilter] = React.useState('all');
  const [propertyFilter, setPropertyFilter] = React.useState('all');
  const [requests, setRequests] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [listError, setListError] = React.useState('');
  const navigate = useNavigate();

  const [detailId, setDetailId] = React.useState(null);
  const [detailLoading, setDetailLoading] = React.useState(false);
  const [detailError, setDetailError] = React.useState('');
  const [detailRequest, setDetailRequest] = React.useState(null);
  const [detailMedia, setDetailMedia] = React.useState([]);

  const [deleteTarget, setDeleteTarget] = React.useState(null);
  const [deleteBusy, setDeleteBusy] = React.useState(false);
  const [toast, setToast] = React.useState(null);

  const [assignTarget, setAssignTarget] = React.useState(null);
  const [staffList, setStaffList] = React.useState([]);
  const [staffLoading, setStaffLoading] = React.useState(false);
  const [assignSaving, setAssignSaving] = React.useState(false);

  const [statusTarget, setStatusTarget] = React.useState(null);
  const [statusChoice, setStatusChoice] = React.useState('pending');
  const [statusSaving, setStatusSaving] = React.useState(false);

  React.useEffect(() => {
    if (!toast) return undefined;
    const t = window.setTimeout(() => setToast(null), 3500);
    return () => window.clearTimeout(t);
  }, [toast]);

  const loadRequests = React.useCallback(async () => {
    if (!supabase) {
      setListError('Supabase is not configured.');
      setRequests([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setListError('');
    const { data, error } = await supabase
      .from('maintenance_requests')
      .select(
        `
        id,
        tenant_name,
        property_name,
        room_label,
        issue_description,
        status,
        logged_on,
        created_at,
        assignee,
        assigned_staff_id,
        contact_info,
        properties (
          name,
          location
        ),
        maintenance_staff (
          id,
          full_name,
          role
        )
      `
      )
      .order('created_at', { ascending: false });

    if (error) {
      setListError(error.message);
      setRequests([]);
    } else {
      setRequests((data || []).map(mapRowToRequest));
    }
    setLoading(false);
  }, []);

  React.useEffect(() => {
    loadRequests();
  }, [loadRequests]);

  React.useEffect(() => {
    if (!detailId) return undefined;

    if (!supabase) {
      setDetailLoading(false);
      setDetailError('Supabase is not configured.');
      setDetailRequest(null);
      setDetailMedia([]);
      return undefined;
    }

    let cancelled = false;
    setDetailLoading(true);
    setDetailError('');
    setDetailRequest(null);
    setDetailMedia([]);

    (async () => {
      const [reqRes, mediaRes] = await Promise.all([
        supabase
          .from('maintenance_requests')
          .select(
            `
            id,
            tenant_name,
            contact_info,
            property_name,
            room_label,
            issue_description,
            status,
            logged_on,
            created_at,
            assignee,
            assigned_staff_id,
            properties (
              name,
              location
            ),
            maintenance_staff (
              id,
              full_name,
              role
            )
          `
          )
          .eq('id', detailId)
          .single(),
        supabase
          .from('maintenance_request_media')
          .select('id, file_url, sort_order')
          .eq('request_id', detailId)
          .order('sort_order', { ascending: true }),
      ]);

      if (cancelled) return;

      if (reqRes.error) {
        setDetailError(reqRes.error.message);
      } else {
        setDetailRequest(reqRes.data);
      }
      if (!mediaRes.error && mediaRes.data) {
        setDetailMedia(mediaRes.data);
      }
      setDetailLoading(false);
    })();

    return () => {
      cancelled = true;
    };
  }, [detailId]);

  React.useEffect(() => {
    const open = Boolean(
      detailId || deleteTarget || assignTarget || statusTarget
    );
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') {
        if (deleteTarget) setDeleteTarget(null);
        else if (assignTarget) setAssignTarget(null);
        else if (statusTarget) setStatusTarget(null);
        else {
          setDetailId(null);
          setDetailRequest(null);
          setDetailMedia([]);
          setDetailError('');
        }
      }
    };
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [detailId, deleteTarget, assignTarget, statusTarget]);

  const closeDetail = () => {
    setDetailId(null);
    setDetailRequest(null);
    setDetailMedia([]);
    setDetailError('');
  };

  const openDetail = (req) => {
    if (!supabase) {
      // eslint-disable-next-line no-alert
      alert('Supabase is not configured.');
      return;
    }
    setDeleteTarget(null);
    setDetailId(req.id);
  };

  const openDelete = (req) => {
    setDetailId(null);
    setDetailRequest(null);
    setDetailMedia([]);
    setDetailError('');
    setDeleteTarget(req);
  };

  const closeDelete = () => setDeleteTarget(null);

  const confirmDelete = async () => {
    if (!deleteTarget || !supabase) return;
    setDeleteBusy(true);
    try {
      const { error } = await supabase
        .from('maintenance_requests')
        .delete()
        .eq('id', deleteTarget.id);
      if (error) throw error;
      setDeleteTarget(null);
      setToast(`Maintenance request removed.`);
      await loadRequests();
    } catch (err) {
      // eslint-disable-next-line no-alert
      alert(err?.message || 'Could not delete request.');
    } finally {
      setDeleteBusy(false);
    }
  };

  const propertyNames = React.useMemo(() => {
    const set = new Set();
    requests.forEach((r) => {
      if (r.property && r.property !== '—') set.add(r.property);
    });
    return ['all', ...Array.from(set).sort()];
  }, [requests]);

  React.useEffect(() => {
    if (propertyFilter !== 'all' && !propertyNames.includes(propertyFilter)) {
      setPropertyFilter('all');
    }
  }, [propertyNames, propertyFilter]);

  const statusLabel = {
    pending: 'Pending',
    in_progress: 'In progress',
    completed: 'Completed',
  };

  const filtered = requests.filter((r) => {
    const term = search.trim().toLowerCase();
    const matchesSearch =
      !term ||
      r.tenant.toLowerCase().includes(term) ||
      r.property.toLowerCase().includes(term) ||
      r.room.toLowerCase().includes(term) ||
      r.issue.toLowerCase().includes(term);
    const matchesStatus =
      statusFilter === 'all' ||
      (statusFilter === 'pending' && r.status === 'pending') ||
      (statusFilter === 'in_progress' && r.status === 'in_progress') ||
      (statusFilter === 'completed' && r.status === 'completed');
    const matchesProperty =
      propertyFilter === 'all' || r.property === propertyFilter;
    return matchesSearch && matchesStatus && matchesProperty;
  });

  const loadStaffForAssign = React.useCallback(async () => {
    if (!supabase) return;
    setStaffLoading(true);
    const { data, error } = await supabase
      .from('maintenance_staff')
      .select('id, full_name, role, email')
      .order('full_name', { ascending: true });
    if (!error && data) setStaffList(data);
    else setStaffList([]);
    setStaffLoading(false);
  }, []);

  const handleAssign = (req) => {
    setAssignTarget(req);
    if (staffList.length === 0) loadStaffForAssign();
  };

  const closeAssignModal = () => {
    setAssignTarget(null);
  };

  const confirmAssignStaff = async (staffId) => {
    if (!assignTarget || !supabase || !staffId) return;
    const member = staffList.find((s) => s.id === staffId);
    const name = (member?.full_name || '').trim() || 'Staff';
    const role = (member?.role || '').trim();
    const assigneeText = role ? `${name} (${role})` : name;
    setAssignSaving(true);
    try {
      const { error } = await supabase
        .from('maintenance_requests')
        .update({
          assigned_staff_id: staffId,
          assignee: assigneeText,
        })
        .eq('id', assignTarget.id);
      if (error) throw error;
      setToast(`Assigned to ${assigneeText}.`);
      closeAssignModal();
      await loadRequests();
    } catch (err) {
      // eslint-disable-next-line no-alert
      alert(
        err?.message ||
          'Could not assign. Run sql/add_maintenance_assigned_staff_supabase.sql in Supabase (adds assigned_staff_id).'
      );
    } finally {
      setAssignSaving(false);
    }
  };

  const closeStatusModal = () => setStatusTarget(null);

  const handleUpdateStatus = (req) => {
    setStatusTarget(req);
    setStatusChoice(req.status || 'pending');
  };

  const confirmStatusUpdate = async () => {
    if (!statusTarget || !supabase) return;
    const next = statusChoice || 'pending';
    if (next === statusTarget.status) {
      closeStatusModal();
      return;
    }
    setStatusSaving(true);
    try {
      const { error } = await supabase
        .from('maintenance_requests')
        .update({ status: next })
        .eq('id', statusTarget.id);
      if (error) throw error;
      setToast(`Status set to ${statusLabel[next] || next}.`);
      closeStatusModal();
      await loadRequests();
      if (detailId === statusTarget.id && detailRequest) {
        setDetailRequest({ ...detailRequest, status: next });
      }
    } catch (err) {
      // eslint-disable-next-line no-alert
      alert(err?.message || 'Could not update status.');
    } finally {
      setStatusSaving(false);
    }
  };

  return (
    <div className="maint-root">
      <UniversalHeader />

      {toast ? (
        <div className="tenants-toast" role="status">
          {toast}
        </div>
      ) : null}

      <div className="maint-shell">
        <header className="maint-header">
          <div>
            <h1 className="maint-title">Maintenance requests</h1>
            <p className="maint-subtitle">
              Track issues from tenants, assign staff, and keep work orders moving.
            </p>
          </div>

          <div className="maint-actions">
            <button
              type="button"
              className="btn-primary maint-btn"
              onClick={() => navigate('/maintenance/new')}
            >
              + New request
            </button>
          </div>
        </header>

        {listError ? (
          <p className="properties-save-error" role="alert">
            {listError}
          </p>
        ) : null}

        <section className="maint-toolbar">
          <div className="maint-search">
            <div className="input-shell maint-search-shell">
              <div className="input-icon">🔍</div>
              <input
                className="input-element"
                type="text"
                placeholder="Search by tenant, property, or issue"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
          </div>

          <div className="maint-filters">
            <select
              className="maint-select"
              value={propertyFilter}
              onChange={(e) => setPropertyFilter(e.target.value)}
            >
              {propertyNames.map((p) => (
                <option key={p} value={p}>
                  {p === 'all' ? 'All properties' : p}
                </option>
              ))}
            </select>

            <div className="maint-status-chips">
              {['all', 'pending', 'in_progress', 'completed'].map((key) => (
                <button
                  key={key}
                  type="button"
                  className={`maint-status-chip ${
                    statusFilter === key ? 'maint-status-chip--active' : ''
                  }`}
                  onClick={() => setStatusFilter(key)}
                >
                  {key === 'all' ? 'All status' : statusLabel[key]}
                </button>
              ))}
            </div>
          </div>
        </section>

        <main className="maint-layout">
          <section className="maint-list">
            <div className="maint-list-header">
              <span className="maint-list-label">Requests</span>
              <span className="maint-list-count">
                {loading ? '…' : `${filtered.length} total`}
              </span>
            </div>

            {loading ? (
              <p className="properties-list-empty">Loading maintenance requests…</p>
            ) : (
              <div className="maint-grid">
                {filtered.length === 0 ? (
                  <p className="properties-list-empty">
                    No maintenance requests yet. Use “+ New request” above.
                  </p>
                ) : (
                  filtered.map((r) => (
                    <article className="maint-card" key={r.id}>
                      <div className="maint-card-main">
                        <div className="maint-avatar">
                          <span>{initials(r.tenant)}</span>
                        </div>
                        <div className="maint-main-text">
                          <h2 className="maint-card-tenant">{r.tenant}</h2>
                          <p className="maint-card-property">
                            {r.property} · Room {r.room}
                          </p>
                          <p className="maint-card-issue">{r.issue}</p>
                        </div>
                        <span className={`maint-status-badge maint-status-badge--${r.status}`}>
                          {statusLabel[r.status]}
                        </span>
                      </div>

                      <div className="maint-card-meta">
                        <div className="maint-meta-block">
                          <span className="maint-meta-label">Logged</span>
                          <span className="maint-meta-value">
                            {r.date
                              ? new Date(r.date).toLocaleDateString()
                              : '—'}
                          </span>
                        </div>
                        <div className="maint-meta-block">
                          <span className="maint-meta-label">Assigned</span>
                          <span className="maint-meta-value">{r.assignee}</span>
                        </div>
                      </div>

                      <div className="maint-card-icon-row">
                        <button
                          type="button"
                          className="properties-icon-btn"
                          aria-label="View request details"
                          onClick={() => openDetail(r)}
                        >
                          👁
                        </button>
                        <button
                          type="button"
                          className="properties-icon-btn"
                          aria-label="Edit request"
                          onClick={() => navigate(`/maintenance/${r.id}/edit`)}
                        >
                          ✏️
                        </button>
                        <button
                          type="button"
                          className="properties-icon-btn properties-icon-btn--danger"
                          aria-label="Delete request"
                          onClick={() => openDelete(r)}
                        >
                          🗑
                        </button>
                      </div>

                      <div className="maint-card-actions">
                        {!r.assignedStaffId ? (
                          <button
                            type="button"
                            className="btn-ghost maint-action-btn"
                            onClick={() => handleAssign(r)}
                          >
                            Assign staff
                          </button>
                        ) : null}
                        <button
                          type="button"
                          className="btn-primary maint-action-btn"
                          onClick={() => handleUpdateStatus(r)}
                        >
                          Update status
                        </button>
                      </div>
                    </article>
                  ))
                )}
              </div>
            )}
          </section>
        </main>
      </div>

      {detailId ? (
        <div
          className="properties-modal-root"
          role="presentation"
          onClick={closeDetail}
        >
          <div
            className="properties-modal-panel"
            role="dialog"
            aria-modal="true"
            aria-labelledby="maint-detail-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="properties-modal-header">
              <h2 id="maint-detail-title" className="properties-modal-title">
                Request details
              </h2>
              <button
                type="button"
                className="properties-modal-close"
                aria-label="Close"
                onClick={closeDetail}
              >
                ×
              </button>
            </div>

            <div className="properties-modal-body">
              {detailLoading ? (
                <p className="properties-list-empty">Loading…</p>
              ) : null}
              {detailError ? (
                <p className="properties-save-error" role="alert">
                  {detailError}
                </p>
              ) : null}
              {!detailLoading && detailRequest ? (
                <>
                  <div className="properties-modal-section">
                    <h3 className="properties-modal-label">Tenant</h3>
                    <p className="properties-modal-value">
                      {detailRequest.tenant_name || '—'}
                    </p>
                  </div>
                  <div className="properties-modal-section">
                    <h3 className="properties-modal-label">Contact</h3>
                    <p className="properties-modal-value">
                      {detailRequest.contact_info || '—'}
                    </p>
                  </div>
                  <div className="properties-modal-section">
                    <h3 className="properties-modal-label">Property</h3>
                    <p className="properties-modal-value">
                      {displayProperty(detailRequest)}
                    </p>
                  </div>
                  <div className="properties-modal-grid">
                    <div className="properties-modal-section">
                      <h3 className="properties-modal-label">Room</h3>
                      <p className="properties-modal-value">
                        {detailRequest.room_label || '—'}
                      </p>
                    </div>
                    <div className="properties-modal-section">
                      <h3 className="properties-modal-label">Status</h3>
                      <p className="properties-modal-value">
                        {statusLabel[detailRequest.status] ||
                          detailRequest.status ||
                          '—'}
                      </p>
                    </div>
                    <div className="properties-modal-section">
                      <h3 className="properties-modal-label">Assigned staff</h3>
                      <p className="properties-modal-value">
                        {assigneeDisplay(detailRequest)}
                      </p>
                    </div>
                  </div>
                  <div className="properties-modal-section">
                    <h3 className="properties-modal-label">Issue</h3>
                    <p className="properties-modal-value">
                      {detailRequest.issue_description || '—'}
                    </p>
                  </div>
                  <div className="properties-modal-section">
                    <h3 className="properties-modal-label">Logged</h3>
                    <p className="properties-modal-value">
                      {detailRequest.logged_on
                        ? new Date(detailRequest.logged_on).toLocaleDateString()
                        : detailRequest.created_at
                          ? new Date(detailRequest.created_at).toLocaleDateString()
                          : '—'}
                    </p>
                  </div>

                  {detailMedia.length > 0 ? (
                    <div className="properties-modal-section">
                      <h3 className="properties-modal-label">Media</h3>
                      <ul className="properties-modal-gallery maint-modal-detail-gallery">
                        {detailMedia.map((m, idx) =>
                          mediaKindFromUrl(m.file_url) === 'video' ? (
                            <li key={m.id}>
                              <video
                                className="maint-media-video"
                                src={m.file_url}
                                controls
                                muted
                                playsInline
                                aria-label={`Video ${idx + 1}`}
                              />
                            </li>
                          ) : (
                            <li key={m.id}>
                              <img
                                src={m.file_url}
                                alt={`Attachment ${idx + 1}`}
                              />
                            </li>
                          )
                        )}
                      </ul>
                    </div>
                  ) : null}
                </>
              ) : null}
            </div>

            <div className="properties-modal-footer">
              <button
                type="button"
                className="btn-primary properties-btn"
                onClick={closeDetail}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {deleteTarget ? (
        <div
          className="tenants-delete-modal-root"
          role="presentation"
          onClick={closeDelete}
        >
          <div
            className="properties-modal-panel tenants-delete-modal-panel"
            role="dialog"
            aria-modal="true"
            aria-labelledby="maint-delete-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="properties-modal-header">
              <h2 id="maint-delete-title" className="properties-modal-title">
                Delete request?
              </h2>
              <button
                type="button"
                className="properties-modal-close"
                aria-label="Close"
                onClick={closeDelete}
              >
                ×
              </button>
            </div>
            <div className="properties-modal-body">
              <p className="properties-modal-value" style={{ margin: 0 }}>
                Permanently remove this maintenance request for{' '}
                <strong>{deleteTarget.tenant}</strong>? This cannot be undone.
              </p>
            </div>
            <div className="properties-modal-footer tenants-delete-modal-footer">
              <button
                type="button"
                className="btn-ghost properties-btn"
                disabled={deleteBusy}
                onClick={closeDelete}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn-primary properties-btn"
                style={{ background: '#b91c1c', borderColor: '#b91c1c' }}
                disabled={deleteBusy}
                onClick={confirmDelete}
              >
                {deleteBusy ? 'Deleting…' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {statusTarget ? (
        <div
          className="properties-modal-root"
          role="presentation"
          onClick={closeStatusModal}
        >
          <div
            className="properties-modal-panel maint-status-update-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="maint-status-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="properties-modal-header">
              <h2 id="maint-status-title" className="properties-modal-title">
                Update status
              </h2>
              <button
                type="button"
                className="properties-modal-close"
                aria-label="Close"
                onClick={closeStatusModal}
              >
                ×
              </button>
            </div>
            <div className="properties-modal-body">
              <p className="properties-modal-value maint-status-modal-context">
                {statusTarget.tenant}
                {(statusTarget.issue || '').trim()
                  ? ` · ${(statusTarget.issue || '').slice(0, 90)}${
                      (statusTarget.issue || '').length > 90 ? '…' : ''
                    }`
                  : ''}
              </p>
              <p className="properties-modal-label" style={{ marginTop: 14 }}>
                New status
              </p>
              <div
                className="maint-status-option-list"
                role="group"
                aria-label="Request status"
              >
                {['pending', 'in_progress', 'completed'].map((key) => (
                  <button
                    key={key}
                    type="button"
                    className={`maint-status-option ${
                      statusChoice === key ? 'maint-status-option--active' : ''
                    }`}
                    onClick={() => setStatusChoice(key)}
                  >
                    <span
                      className={`maint-status-badge maint-status-badge--${key}`}
                    >
                      {statusLabel[key]}
                    </span>
                  </button>
                ))}
              </div>
            </div>
            <div className="properties-modal-footer">
              <button
                type="button"
                className="btn-ghost properties-btn"
                disabled={statusSaving}
                onClick={closeStatusModal}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn-primary properties-btn"
                disabled={statusSaving}
                onClick={confirmStatusUpdate}
              >
                {statusSaving ? 'Saving…' : 'Save status'}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      <MaintenanceAssignStaffModal
        open={Boolean(assignTarget)}
        title="Assign task to staff"
        subtitle={
          assignTarget
            ? `${assignTarget.tenant} · ${(assignTarget.issue || '').slice(0, 100)}${
                (assignTarget.issue || '').length > 100 ? '…' : ''
              }`
            : ''
        }
        staff={staffList}
        staffLoading={staffLoading}
        saving={assignSaving}
        onClose={closeAssignModal}
        onConfirm={confirmAssignStaff}
      />
    </div>
  );
}

export default Maintenance;
